/**
 * Nguồn cấu hình chung cho điều hướng login/logout.
 *
 * - Thay đổi trang đích sau logout tại LOGOUT_REDIRECT_PATH.
 * - Thêm hoặc bỏ route bắt buộc đăng nhập tại PROTECTED_ROUTE_PREFIXES.
 * - Các tương tác public cần login tại chỗ, như "Ứng tuyển" ở /viec-lam,
 *   được xử lý bởi component của feature và không thuộc danh sách protected.
 *
 * Xem docs/features/authentication.md để biết đầy đủ luồng và các điểm tích hợp.
 */
export const AUTH_HOME_PATH = '/';
export const LOGOUT_REDIRECT_PATH = AUTH_HOME_PATH;

export const AUTH_REQUIRED_QUERY_PARAM = 'auth';
export const AUTH_REQUIRED_QUERY_VALUE = 'required';
export const AUTH_RETURN_TO_QUERY_PARAM = 'returnTo';

export const PROTECTED_ROUTE_PREFIXES = [
  '/tao-cv/edit',
  '/ho-so',
  '/quan-ly-trang-thai-ung-tuyen',
] as const;

/** Terminal account-action routes should not be restored after login. */
const ACCOUNT_ACTION_SUCCESS_PATH = '/setting-success';
const NON_RETURNABLE_AUTH_PATHS = [ACCOUNT_ACTION_SUCCESS_PATH] as const;

export function isProtectedAuthRoute(pathname: string) {
  return PROTECTED_ROUTE_PREFIXES.some(
    route => pathname === route || pathname.startsWith(`${route}/`)
  );
}

export function getSessionExpiredCloseRedirect(pathname: string) {
  return isProtectedAuthRoute(pathname) ? AUTH_HOME_PATH : undefined;
}

export function shouldOpenSessionExpiredLogin(pathname: string, search = '') {
  return !(
    pathname === ACCOUNT_ACTION_SUCCESS_PATH &&
    new URLSearchParams(search).get('type') === 'delete-account'
  );
}

export function getSessionExpiredReturnTo(
  pathname: string,
  search = '',
  hash = ''
) {
  if (NON_RETURNABLE_AUTH_PATHS.some(route => pathname === route)) {
    return AUTH_HOME_PATH;
  }

  return `${pathname}${search}${hash}`;
}

export function buildAuthRequiredPath(pathname: string, search = '') {
  const searchParams = new URLSearchParams({
    [AUTH_REQUIRED_QUERY_PARAM]: AUTH_REQUIRED_QUERY_VALUE,
    [AUTH_RETURN_TO_QUERY_PARAM]: `${pathname}${search}`,
  });

  return `${AUTH_HOME_PATH}?${searchParams.toString()}`;
}

export function getSafeAuthReturnTo(value?: string) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return AUTH_HOME_PATH;
  }

  const [pathname] = value.split(/[?#]/, 1);

  return isProtectedAuthRoute(pathname) ? value : AUTH_HOME_PATH;
}

export function getSafeLocalReturnTo(value?: string) {
  if (!value || value.length > 2048 || !value.startsWith('/')) {
    return AUTH_HOME_PATH;
  }

  try {
    const safeOrigin = 'http://upzi.local';
    const url = new URL(value, safeOrigin);

    if (url.origin !== safeOrigin) return AUTH_HOME_PATH;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return AUTH_HOME_PATH;
  }
}
